import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { shopifyAuth } from '../services/shopifyAuth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      console.error('Firebase auth is not initialized');
      setLoading(false);
      setCurrentUser(null);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = () => {};

    const applyUser = async (user) => {
      if (cancelled) return;
      if (user) {
        try {
          const token = await user.getIdToken(false);
          if (cancelled) return;
          localStorage.setItem('token', token);
          setCurrentUser(user);
        } catch (error) {
          console.error('Error reading the signed-in session:', error);
          if (cancelled) return;
          localStorage.removeItem('token');
          setCurrentUser(null);
        }
      } else {
        localStorage.removeItem('token');
        setCurrentUser(null);
      }
      setLoading(false);
    };

    (async () => {
      try {
        // Wait until Firebase has restored a persisted session. The first
        // callback can otherwise be null and the app sends a signed-in user
        // to the login page.
        if (auth.authStateReady) {
          await auth.authStateReady();
        }
      } catch (error) {
        console.error('Auth state failed to settle:', error);
      }
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, (user) => {
        applyUser(user);
      }, (error) => {
        console.error('Firebase auth listener error:', error);
        if (!cancelled) {
          setLoading(false);
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const initiateShopifyAuth = async () => {
    try {
      const authUrl = await shopifyAuth.initiateAuth();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Shopify auth:', error);
      throw error;
    }
  };

  const handleShopifyCallback = async (code) => {
    try {
      await shopifyAuth.handleCallback(code);
    } catch (error) {
      console.error('Failed to handle Shopify callback:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Clear any stored tokens or session data
      localStorage.removeItem('token');
      
      // In development mode with mock auth, just clear the user
      if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_API_KEY === undefined) {
        setCurrentUser(null);
        return;
      }
      
      await signOut(auth);
    } catch (error) {
      console.error('Failed to log out:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    initiateShopifyAuth,
    handleShopifyCallback,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 